// Cookie consent storage + region detection + GA4 gate.
// GA4 stays dormant until a Measurement ID is set below AND analytics consent is granted.
export const GA4_MEASUREMENT_ID = ""; // e.g. "G-XXXXXXX" — leave empty to keep GA4 disabled.

export const CONSENT_VERSION = 1;
export const CONSENT_KEY = "punchink_cookie_consent";

export type ConsentCategories = {
  necessary: true; // always on
  analytics: boolean;
};

export type StoredConsent = {
  version: number;
  timestamp: string;
  region: "eu" | "us" | "other";
  categories: ConsentCategories;
};

export function loadConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveConsent(c: StoredConsent) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(c));
  window.dispatchEvent(new CustomEvent("cookie-consent-change", { detail: c }));
}

// EU/EEA + UK ISO-3166-1 alpha-2 codes (strict opt-in region).
const EU_UK_CODES = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT","LV",
  "LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE","IS","LI","NO","GB","UK",
]);

// EU/UK timezone hints for offline / IP-lookup-blocked visitors.
const EU_UK_TZ_HINTS = [
  "Europe/", "Atlantic/Reykjavik", "Atlantic/Canary", "Atlantic/Madeira", "Atlantic/Azores",
];

export type Region = "eu" | "us" | "other";

export async function detectRegion(): Promise<Region> {
  // 1) Cheap timezone signal.
  let tz = "";
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { /* ignore */ }
  const tzIsEu = EU_UK_TZ_HINTS.some((p) => tz.startsWith(p));
  const tzIsUs = tz.startsWith("America/") && !tz.includes("Argentina") && !tz.includes("Sao_Paulo");

  // 2) Best-effort IP geolocation (fails silently, no throw).
  try {
    const r = await fetch("https://get.geojs.io/v1/ip/country.json", { cache: "no-store" });
    if (r.ok) {
      const j = await r.json();
      const cc = String(j?.country || "").toUpperCase();
      if (EU_UK_CODES.has(cc)) return "eu";
      if (cc === "US") return "us";
      if (cc) return "other";
    }
  } catch { /* ignore */ }

  if (tzIsEu) return "eu";
  if (tzIsUs) return "us";
  return "other"; // Unknown -> treated strictly (opt-in) by the banner.
}

export function analyticsAllowed(): boolean {
  const c = loadConsent();
  return !!c?.categories.analytics;
}

// Idempotent GA4 loader gated on consent + Measurement ID.
let gaLoaded = false;
export function maybeInitGA4() {
  if (gaLoaded) return;
  if (!GA4_MEASUREMENT_ID) return; // dormant until an ID is provided
  if (!analyticsAllowed()) return;
  gaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(s);
  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) { (window as any).dataLayer.push(args); }
  (window as any).gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA4_MEASUREMENT_ID, { anonymize_ip: true });
}
